import { Box, Breadcrumbs, ImageList, ImageListItem, ImageListItemBar, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useState } from "react";
import { FullScreen, useFullScreenHandle } from "react-full-screen";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { LayoutState, MediaQueryLinebreak, MediaQueryMobile, MediaQueryShallow, MediaQuerySmall1, MediaQuerySmall2, MediaQuerySmall3, MediaQuerySmall4, MediaQuerySmall5, PicDimH, PicDimW, PicWidth, Scrolls, ScrollToPosition } from "../Layout";
import { hideKrpano } from "../Util";
import i18n, { ui, Workdesc } from "../i18n";
import { change } from "../state/Nav";
import { RootState } from "../store";
import { ImageDesc, itemDataAll } from "./BookData";
import Painting from "./Painting";

type Lang = "de" | "en";

const landscape = "landscape";
const portrait = "portrait";

const size = PicWidth;
const wh = size * PicDimW / PicDimH;

// The behavior of the component is controlled by two elements:
//     paintingName: name of the painting shown ("" = no painting)
//     params.serie: name of the series shown ("all" = summary of series)
export default function Books() {
    // Init paintingName
    const [paintingName, setPaintingName] = useState("");

    // Get input params
    const params = useParams();

    const isPainting = (paintingName !== "");

    // Init layout parameters
    const layoutState = new LayoutState();
    layoutState.isMobile = useMediaQuery(MediaQueryMobile);
    layoutState.isSmall1 = useMediaQuery(MediaQuerySmall1);
    layoutState.isSmall2 = useMediaQuery(MediaQuerySmall2);
    layoutState.isSmall3 = useMediaQuery(MediaQuerySmall3);
    layoutState.isSmall4 = useMediaQuery(MediaQuerySmall4);
    layoutState.isSmall5 = useMediaQuery(MediaQuerySmall5);
    layoutState.linebreak = useMediaQuery(MediaQueryLinebreak);
    layoutState.isShallow = useMediaQuery(MediaQueryShallow);

    // Determine UI language
    const lang = useSelector((state: RootState) => state.language.value);

    // Set navigation to current component
    const dispatch = useDispatch();
    dispatch(change("books"));

    // Hide virtual tour
    hideKrpano();

    // Scroll to previous position
    ScrollToPosition("booksAll");
    Scrolls.positions.set("book", 0);

    // Required hooks
    const navigate = useNavigate();
    const handle = useFullScreenHandle();
    const theme = useTheme();

    // Construct the net data to be shown: heading and item data
    // Init with the summary series
    let heading = ui[lang].Gallery.heading;
    let itemData = itemDataAll;

    // Separate the images in the series according to orientation
    let imageDataPortrait: ImageDesc[] = [];
    let imageDataLandscape: ImageDesc[] = [];
    itemData.map((item) => {
        if (item.format === landscape) imageDataLandscape.push(item);
        if (item.format === portrait) imageDataPortrait.push(item);
    });

    // Resort items so that portraits come first
    itemData = [];
    for (var i = 0; i < imageDataPortrait.length; i++) {
        itemData.push(imageDataPortrait[i]);
    }
    for (var i = 0; i < imageDataLandscape.length; i++) {
        itemData.push(imageDataLandscape[i]);
    }

    // Calculate number of rows with portraits
    const nrRowsPortrait = Math.floor((imageDataPortrait.length - 1) / 3) + 1;

    const fullsize = handle.active && isPainting;

    // Determine parameters for current painting (if any)
    let w = theme.painting.landscape.width;
    let h = theme.painting.landscape.height;
    let workdesc: Workdesc;
    if (isPainting) {
        workdesc = i18n(paintingName);
        if (workdesc.format === portrait) {
            w = theme.painting.portrait.width;
            h = theme.painting.portrait.height;
        }
        if (w * window.visualViewport!.height > h * window.visualViewport!.width) {
            h = h * window.visualViewport!.width / w;
            w = window.visualViewport!.width;
        }
        else {
            w = w * window.visualViewport!.height / h;
            h = window.visualViewport!.height;
        }
    }

    // Init running row index and number of columns in current row
    let rowIdx = 1;
    let currentCols = 0;

    return (
        <Box>
            {!layoutState.isMobile &&
                <Box>
                    <Box sx={{ maxWidth: layoutState.GalleryMaxWidth(), paddingTop: layoutState.GalleryPaddingTop(), paddingLeft: layoutState.GalleryPaddingLeft(), paddingBottom: "10px" }}>
                        <Breadcrumbs separator="›">
                             <Typography variant="body2" sx={{ fontSize: "0.925rem", color: "black" }}>
                                {heading}
                            </Typography>
                        </Breadcrumbs>
                    </Box>

                    <Box sx={{ width: layoutState.GalleryWidth(), marginTop: "16px" }}>
                        <ImageList rowHeight={layoutState.fscale() * 550} key={Math.floor(Math.random() * 1000)} sx={{ width: "100%", padding: layoutState.GalleryPaddingImageList(), margin: 0 }} cols={15} gap={layoutState.scale() * 45} >
                            {itemData.map((item, idx) => {
                                let w = (item.format === portrait) ? wh : size;
                                const h = (item.format === portrait) ? size : wh;
                                let title = item.title[lang as Lang];

                                let cols = (item.format === portrait) ? 5 : 7;
                                if (currentCols + cols > 15) {
                                    currentCols = cols;
                                    rowIdx++;
                                }
                                else {
                                    currentCols += cols;
                                }
                                const ytrans = (rowIdx <= nrRowsPortrait + 1) ? 0 : (rowIdx - nrRowsPortrait - 1) * 150 * layoutState.scale();

                                return (
                                    <ImageListItem cols={cols} key={item.name} sx={{ transform: "translate(0px,-" + ytrans + "px)" }}>
                                        <img
                                            src={"/book/" + lang + "/" + item.name + "/" + item.image + "01.jpg" + "?versionNr=" + item.versionNr}
                                            loading="lazy"
                                            style={{ maxWidth: layoutState.scale() * w, maxHeight: layoutState.scale() * h, margin: "0", cursor: "pointer", boxShadow: "0px 3px 5px -1px rgba(0, 0, 0, 0.2), 0px 5px 8px 0px rgba(0, 0, 0, 0.14), 0px 1px 14px 0px rgba(0, 0, 0, 0.12)", marginBottom: "0px" }}
                                            onClick={() => {
                                                if (params.serie === "all") {
                                                    navigate("/gallery/" + item.name);
                                                }
                                                else {
                                                    ScrollToPosition("book");
                                                    navigate("/book/" + item.name);
                                                }
                                            }}
                                        />
                                        <ImageListItemBar position="below" sx={{ marginLeft: "0%", alignItems: "center", paddingTop: (10 * layoutState.scale()) + "px", paddingBottom: "0px", textAlign: "center", maxWidth: "100%", background: "inherit" }}
                                            title={title}
                                        />
                                    </ImageListItem>
                                )
                            }
                            )}
                        </ImageList>
                    </Box>
                </Box>
            }

            {layoutState.isMobile &&
                <Box>
                    <Box sx={{ maxWidth: layoutState.GalleryMaxWidth(), paddingTop: layoutState.GalleryPaddingTop(), paddingLeft: layoutState.GalleryPaddingLeft() }}>
                        <Breadcrumbs separator="›">
                             <Typography variant="body2" sx={{ fontSize: "0.925rem", display: "block" }}>
                                {heading}
                            </Typography>
                        </Breadcrumbs>
                    </Box>

                    <Box sx={{}}>
                        <ImageList sx={{ padding: layoutState.GalleryPaddingImageList() }} cols={1} gap={45} >
                            {itemData.map((item) => (
                                <ImageListItem key={item.name} >
                                    <img
                                        src={"/book/" + item.name + "/" + item.image + "01.jpg"}
                                        loading="lazy"
                                        style={{ cursor: "pointer", boxShadow: "0px 3px 5px -1px rgba(0, 0, 0, 0.2), 0px 5px 8px 0px rgba(0, 0, 0, 0.14), 0px 1px 14px 0px rgba(0, 0, 0, 0.12)" }}
                                        onClick={() => {
                                                 navigate("/book/" + item.name);
                                            
                                        }}
                                    />

                                    <ImageListItemBar position="below" sx={{ paddingTop: "10px", textAlign: "center", maxWidth: "100%", background: "inherit" }}
                                        title={item.title[lang as Lang]}
                                    />
                                </ImageListItem>
                            ))}
                        </ImageList>
                    </Box >
                    <FullScreen handle={handle}>
                        {fullsize &&
                            <Painting name={paintingName} fromTour={false} fullscreenhandle={handle}></Painting>
                        }
                    </FullScreen>
                </Box>
            }
        </Box>
    );
}


